import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useDI } from "@/infrastructure/di/useDI";
import { asJobId } from "@/domain/model/Ids";
import type { JobDetails } from "@/domain/model/JobDetails";
import type { TimelineEvent } from "./JobTimelineTypes";

import { JobTimeline } from "./JobTimeline";

// MUI
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import PlaceIcon from "@mui/icons-material/Place";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import DrawIcon from "@mui/icons-material/Draw";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  PageContent,
  SectionCard,
  PageHeader,
  SummaryStatCard,
} from "@/adapters/inbound/layout";
import { Title } from "react-admin";

function formatAddress(
  a?: {
    street: string;
    streetNumber: string;
    zip: string;
    city: string;
  } | null,
) {
  if (!a) return "—";
  return `${a.street} ${a.streetNumber}, ${a.zip} ${a.city}`;
}

function formatDateTime(date: Date) {
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function normalizeForUi(state: JobDetails["job"]["jobState"]) {
  // ON_BREAK should behave like IN_PROGRESS in the web UI
  if (state === "ON_BREAK") return "IN_PROGRESS";
  return state;
}

function statusColor(state: JobDetails["job"]["jobState"]) {
  const status = normalizeForUi(state);

  switch (status) {
    case "COMPLETED":
      return "success" as const;

    case "CLOSED_FOR_DAY":
      return "info" as const;

    case "INTERRUPTED":
      return "error" as const;

    case "IN_PROGRESS":
      return "warning" as const;

    case "OPEN":
    default:
      return "default" as const;
  }
}

function statusLabel(state: JobDetails["job"]["jobState"]) {
  const status = normalizeForUi(state);

  switch (status) {
    case "OPEN":
      return "Offen";

    case "IN_PROGRESS":
      return "In Arbeit";

    case "INTERRUPTED":
      return "Unterbrochen";

    case "CLOSED_FOR_DAY":
      return "Feierabend";

    case "COMPLETED":
      return "Abgeschlossen";

    default:
      return status;
  }
}

function formatDuration(seconds?: number | null) {
  if (!seconds || seconds <= 0) return "—";
  const totalMin = Math.round(seconds / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m} min`;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

function readinessChip(readiness: JobDetails["billing"]["readiness"]) {
  switch (readiness) {
    case "READY":
      return {
        label: "Bereit zur Abrechnung",
        color: "success" as const,
        icon: <CheckCircleIcon fontSize="small" />,
      };
    case "NEEDS_REVIEW":
      return {
        label: "Prüfen empfohlen",
        color: "warning" as const,
        icon: <WarningAmberIcon fontSize="small" />,
      };
    case "NOT_READY":
    default:
      return {
        label: "Nicht abrechnungsbereit",
        color: "error" as const,
        icon: <ErrorOutlineIcon fontSize="small" />,
      };
  }
}

function missingLabel(code: JobDetails["billing"]["missing"][number]["code"]) {
  switch (code) {
    case "SIGNATURE_MISSING":
      return "Unterschrift fehlt";
    case "NO_WORK_TIME":
      return "Keine Arbeitszeit erfasst";
    case "NO_EVIDENCE":
      return "Keine Nachweise (Foto/Notiz) vorhanden";
    case "JOB_NOT_COMPLETED":
      return "Auftrag nicht abgeschlossen / kein Tagesabschluss";
    default:
      return code;
  }
}

function warningLabel(code: JobDetails["billing"]["warnings"][number]["code"]) {
  switch (code) {
    case "TRAVEL_LOW_CONFIDENCE":
      return "Anfahrt: geringe Sicherheit";
    case "TRAVEL_NOT_DETECTED":
      return "Anfahrt: keine Fahrt erkannt";
    case "GPS_DATA_SPARSE":
      return "GPS-Daten lückenhaft";
    case "LONG_INTERRUPTIONS":
      return "Lange Unterbrechungen";
    default:
      return code;
  }
}

function warningColor(
  sev: JobDetails["billing"]["warnings"][number]["severity"],
) {
  switch (sev) {
    case "ERROR":
      return "error" as const;
    case "WARNING":
      return "warning" as const;
    case "INFO":
    default:
      return "default" as const;
  }
}

function BillingSummaryCard(props: { billing: JobDetails["billing"] }) {
  const { billing } = props;
  const chip = readinessChip(billing.readiness);

  // Your time dto includes: workSeconds, pauseSeconds, suspendSeconds, activeSeconds
  const work = billing.time?.workSeconds ?? 0;
  const interrupt = billing.time?.suspendSeconds ?? 0;
  const travel = billing.travel?.totalTravelSeconds ?? 0;

  // trust hint for travel
  const travelHint =
    billing.travel?.verdict === "USE"
      ? `Sicherheit ${Math.round((billing.travel.confidence ?? 0) * 100)}%`
      : billing.travel?.verdict === "REVIEW"
        ? "prüfen"
        : "nicht verwenden";

  return (
    <SectionCard
      title="Abrechnung"
      actions={
        <Chip
          icon={chip.icon}
          label={chip.label}
          color={chip.color}
          sx={{ fontWeight: 700 }}
        />
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Arbeitszeit
            </Typography>
          </Stack>
          <Typography variant="h6" fontWeight="bold">
            {formatDuration(work)}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DirectionsCarIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Anfahrt
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="h6" fontWeight="bold">
              {formatDuration(travel)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {travel > 0 ? travelHint : "—"}
            </Typography>
          </Stack>
          {billing.travel?.detectedTrips != null &&
            billing.travel.detectedTrips > 0 && (
              <Typography variant="caption" color="text.secondary">
                {billing.travel.detectedTrips} Fahrt(en) erkannt
              </Typography>
            )}
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PauseCircleOutlineIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Unterbrechung
            </Typography>
          </Stack>
          <Typography variant="h6" fontWeight="bold">
            {formatDuration(interrupt)}
          </Typography>
        </Grid>
      </Grid>

      {(billing.missing?.length ?? 0) > 0 ||
      (billing.warnings?.length ?? 0) > 0 ? (
        <>
          <Divider sx={{ my: 2 }} />

          {(billing.missing?.length ?? 0) > 0 && (
            <Box mb={1.5}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Fehlend
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {billing.missing.map((m, idx) => (
                  <Chip
                    key={`${m.code}-${idx}`}
                    label={missingLabel(m.code)}
                    color="error"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          )}

          {(billing.warnings?.length ?? 0) > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Hinweise
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {billing.warnings.map((w, idx) => (
                  <Chip
                    key={`${w.code}-${idx}`}
                    label={warningLabel(w.code)}
                    color={warningColor(w.severity)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1.5 }}
        >
          Keine offenen Punkte erkannt.
        </Typography>
      )}
    </SectionCard>
  );
}

export function JobDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { jobDetailsService } = useDI();

  const [details, setDetails] = useState<JobDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await jobDetailsService.getJobDetails(asJobId(id));
        setDetails(data);
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Failed to fetch or map JobDetails:", err);
          setError(
            "Fehler beim Laden des Auftrags. Bitte versuchen Sie es später erneut.",
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
    return;
  }, [id, jobDetailsService]);

  const jobId = id ?? "";

  // Keep timeline visual flow consistent: show last N events, chronological
  const recentTimelineEvents: TimelineEvent[] = useMemo(() => {
    const ev = details?.recentEvents ?? [];
    const sortedAsc = [...ev].sort(
      (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime(),
    );
    return sortedAsc.slice(-12); // last 12, still ascending
  }, [details]);

  if (isLoading) {
    return (
      <PageContent width="content">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </PageContent>
    );
  }

  if (error || !details) {
    return (
      <PageContent width="content">
        <Alert severity="error">{error || "Auftrag nicht gefunden."}</Alert>
      </PageContent>
    );
  }

  return (
    <PageContent width="content">
      <Title title="Auftragsdetails"/>
      {/* Header + Actions */}
      <Stack spacing={3}>
        <PageHeader
          title={details.job.title || "Auftrag"}
          subtitle={`Kunde: ${details.context.customer.name}`}
          actions={
            <Stack
              spacing={1}
              alignItems={{ xs: "stretch", md: "flex-end" }}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <Chip
                label={statusLabel(details.job.jobState)}
                color={statusColor(details.job.jobState)}
                variant="outlined"
                sx={{ fontWeight: 600, height: 32 }}
              />

              <Button
                variant="contained"
                component={Link}
                to={`/dashboard/jobs/${jobId}/report`}
                startIcon={<DescriptionIcon />}
                fullWidth
                sx={{ minWidth: { md: 200 } }}
              >
                Auftragsbericht
              </Button>

              <Button
                variant="outlined"
                component={Link}
                to={`/dashboard/jobs/${jobId}`}
                startIcon={<EditIcon />}
                fullWidth
                sx={{ minWidth: { md: 200 } }}
              >
                Bearbeiten
              </Button>
            </Stack>
          }
        />

        {/* Context Card */}
        <SectionCard>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight="bold"
                display="block"
              >
                Kunde
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="bold">
                  {details.context.customer.name}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {formatAddress(details.context.customer.address ?? null)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight="bold"
                display="block"
              >
                Einsatzort
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <PlaceIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="bold">
                  {details.context.site.label || "Baustelle"}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {formatAddress(details.context.site.address ?? null)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight="bold"
                display="block"
              >
                Planung
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonthIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {details.job.plannedStartAt
                    ? formatDateTime(details.job.plannedStartAt)
                    : "—"}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                Letztes Ereignis:{" "}
                {details.job.lastEventAt
                  ? formatDateTime(details.job.lastEventAt)
                  : "—"}
              </Typography>
            </Grid>
          </Grid>

          {(details.job.description || details.job.notes) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight="bold"
                    display="block"
                  >
                    Beschreibung
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {details.job.description || "—"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight="bold"
                    display="block"
                  >
                    Notizen (intern)
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {details.job.notes || "—"}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </SectionCard>

        {/* Stats */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <SummaryStatCard
              icon={<PhotoCameraIcon />}
              label="Fotos"
              value={details.stats.photoCount}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <SummaryStatCard
              icon={<NoteAltIcon />}
              label="Notizen"
              value={details.stats.noteCount}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <SummaryStatCard
              icon={<DrawIcon />}
              label="Unterschriften"
              value={details.stats.signatureCount}
            />
          </Grid>
        </Grid>

        {/* Billing */}

        <BillingSummaryCard billing={details.billing} />

        {/* Timeline (reused, uniform) */}
        <SectionCard
          title="Letzte Ereignisse"
          actions={
            <Typography variant="caption" color="text.secondary">
              Anzeige: letzte {Math.min(12, details.recentEvents.length)} von{" "}
              {details.recentEvents.length}
            </Typography>
          }
        >
          <JobTimeline events={recentTimelineEvents} />
        </SectionCard>
      </Stack>
    </PageContent>
  );
}
